import type { ReactNode } from 'react';
import { hasOrgFeature, type OrgFeature as OrgFeatureName, type OrgType } from '@siliconx/shared';
import { useAuth } from './authContext';

/**
 * SIL-1034: the customer-portal half of the org-feature gating primitive
 * (`@siliconx/shared`'s `hasOrgFeature`), replacing SIL-1019/SIL-1022's
 * per-page `organizationType === 'individual'`/`!isIndividual` checks
 * scattered across route/component files. `useOrgFeatureState` is the SOLE
 * function that reads `useAuth()` to make a feature decision — every other
 * hook/component here is a thin wrapper over it, so there is exactly one
 * place identity-loading state is translated into a gating decision.
 *
 * 'pending' (not just true/false) matters: while identity is loading (or
 * failed to load — `organizationType` is `null` in both cases,
 * authContext.tsx), a feature must NOT be treated as unavailable — that
 * would flash a "not available" state or fire a redirect before identity
 * has even resolved. Callers that only care about the boolean use
 * `useOrgFeature`, which treats 'pending' as false (safe: it just means
 * "don't show/fetch yet"); callers that need to distinguish loading from a
 * genuine deny (the root-level redirect gate, __root.tsx) use
 * `useOrgFeatureState` directly.
 */
export type OrgFeatureState = 'pending' | 'enabled' | 'disabled';

/** Pure, hooks-free — usable from a non-component gate function (see __root.tsx's OrgFeatureGate). */
export function orgFeatureStateOf(
  auth: { isLoading: boolean; organizationType: OrgType | null },
  feature: OrgFeatureName,
): OrgFeatureState {
  if (auth.isLoading || auth.organizationType == null) return 'pending';
  return hasOrgFeature(auth.organizationType, feature) ? 'enabled' : 'disabled';
}

export function useOrgFeatureState(feature: OrgFeatureName): OrgFeatureState {
  const { isLoading, organizationType } = useAuth();
  return orgFeatureStateOf({ isLoading, organizationType }, feature);
}

/** Convenience boolean — 'pending' reads as false (don't show/fetch yet, not a deny). */
export function useOrgFeature(feature: OrgFeatureName): boolean {
  return useOrgFeatureState(feature) === 'enabled';
}

/**
 * Declarative conditional-render wrapper over `useOrgFeature` — renders
 * `children` only once the feature is confirmed `enabled`; `fallback`
 * (default `null`) renders otherwise, including while 'pending'.
 */
export function OrgFeature({
  name,
  children,
  fallback = null,
}: {
  name: OrgFeatureName;
  children: ReactNode;
  fallback?: ReactNode;
}): ReactNode {
  return useOrgFeature(name) ? children : fallback;
}
